import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiClock, FiMoreHorizontal, FiPlus, FiTrash2 } from 'react-icons/fi';
import { AlarmClock, CalendarClock, CalendarPlus, Timer } from 'lucide-react';
import { getFollowUpMetaLine } from './followUpUtils';
import type { FollowUpDisplay, PostponePreset } from './types';
import './followups-panel.css';

type FollowUpRowProps = {
  item: FollowUpDisplay;
  onComplete: (id: string) => void;
  onPostpone: (id: string, preset: PostponePreset) => void;
  onDelete: (id: string) => void;
  showActions?: boolean;
};

const POSTPONE_OPTIONS: { preset: PostponePreset; labelKey: string; Icon: typeof CalendarClock }[] = [
  { preset: 'tomorrow', labelKey: 'todayPanel.postponeTomorrow', Icon: CalendarClock },
  { preset: 'next_week', labelKey: 'todayPanel.postponeNextWeek', Icon: CalendarPlus },
];

export default function FollowUpRow({
  item,
  onComplete,
  onPostpone,
  onDelete,
  showActions = true,
}: FollowUpRowProps) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  const done = item.status === 'DONE' || item.dueTone === 'done';
  const metaLine = getFollowUpMetaLine(item);
  const isOverdue = item.dueTone === 'overdue' && !done;

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (itemRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  return (
    <div ref={itemRef} className={`fu-item${done ? ' done' : ''}`}>
      <button
        type="button"
        className="fu-check"
        onClick={() => !done && onComplete(item.id)}
        disabled={done}
        aria-label={done ? t('todayPanel.completed') : t('todayPanel.markDone')}
      >
        <FiCheck strokeWidth={3} />
      </button>

      <div className="fu-body">
        <div className="fu-t">{item.title}</div>
        {!done && (
          <div className={`fu-meta${isOverdue ? ' overdue' : ''}`}>
            {isOverdue ? <AlarmClock /> : <FiClock />}
            {metaLine}
          </div>
        )}
      </div>

      {!done && showActions && (
        <>
          <button
            type="button"
            className="fu-rowact"
            title={t('todayPanel.postponeTomorrow')}
            aria-label={t('todayPanel.postponeTomorrow')}
            onClick={() => onPostpone(item.id, 'tomorrow')}
          >
            <Timer />
          </button>
          <button
            type="button"
            className="fu-rowact"
            aria-label={t('todayPanel.postpone')}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
          >
            <FiMoreHorizontal />
          </button>
          {menuOpen && (
            <div className="fu-menu" onClick={(e) => e.stopPropagation()}>
              {POSTPONE_OPTIONS.map(({ preset, labelKey, Icon }) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => {
                    onPostpone(item.id, preset);
                    setMenuOpen(false);
                  }}
                >
                  <Icon strokeWidth={1.75} />
                  {t(labelKey)}
                </button>
              ))}
              <hr />
              <button
                type="button"
                className="del"
                onClick={() => {
                  onDelete(item.id);
                  setMenuOpen(false);
                }}
              >
                <FiTrash2 />
                {t('common.delete')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
